/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import {ErrorType, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useState} from 'react';
import {COUNTRIES} from '../../../../util/countryUtil';
import {ActivateHandle, VATType, VATValue} from '../../../../util/interfaces';
import {updateDefaultVATs} from '../../../../util/requestSender';
import {createErrorMessage} from '../../../../util/utils';
import {CountryVATValues, CustomVAT, DefaultVAT} from '../../../../util/vatUtil';
import {AppContext} from '../../../misc/appContext';
import DefaultVATSettings from './defaultVATSettings';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: (values: CountryVATValues) => void;
}

function DefaultVATSettingsController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const {onSuccess} = props;
    const [active, setActive] = useState(false);
    const [countryValues, setCountryValues] = useState(new CountryVATValues());
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, ErrorType>>({});
    const [availableCountries, setAvailableCountries] = useState<Array<[string, string]>>([]);

    function onActivate(values: CountryVATValues) {
        setCountryValues(values.clone());
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setCountryValues(new CountryVATValues());
        setErrors({});
        setLoading(false);
        setAvailableCountries([]);
    }

    function onAddCountry(countryCode: string) {
        setCountryValues(countryValues.clone().setCountryValue(countryCode, DefaultVAT(0)));
    }

    function onDeleteCountry(countryCode: string) {
        return function () {
            setCountryValues(countryValues.clone().deleteCountryValue(countryCode));
            if (Object.hasOwn(errors, countryCode)) {
                const _errors = {...errors};
                delete _errors[countryCode];
                setErrors(_errors);
            }
        };
    }

    function onClickChange(countryCode: string, value: VATValue) {
        return function () {
            onChange(countryCode)(value);
        };
    }

    function onChange(countryCode: string) {
        return function (value: VATValue | number) {
            if (typeof value === 'number') value = CustomVAT(value);
            setCountryValues(countryValues.clone().setCountryValue(countryCode, value));
            setErrors({...errors, [countryCode]: null});
        };
    }

    function onSubmit(retroactive?: boolean) {
        return function () {
            if (!account || !validateValues()) return;
            if (retroactive === undefined) {
                const message = t(
                    'settings.vat.retroactiveMsg',
                    'Wil je de gewijzigde BTW percentages met terugwerkende kracht toepassen? Hiermee worden alle facturen waar de gewijzigde producten op staan bijgewerkt.'
                );
                return showDialog(message, t('settings.vat.applyChanges', 'Wijzigingen toepassen'), [
                    {
                        label: t('settings.vat.noNewInvoicesOnly', 'Nee, alleen voor nieuwe facturen'),
                        onClick: onSubmit(false)
                    },
                    {
                        label: t('settings.vat.yesRetroactive', 'Ja, met terugwerkende kracht'),
                        onClick: onSubmit(true),
                        primary: false
                    }
                ]);
            }
            setLoading(true);
            updateDefaultVATs({
                data: {
                    account_id: account.id,
                    default_vats: countryValues.dump(true),
                    apply_retroactive: retroactive
                },
                callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        onSuccess(countryValues);
                        onClose();
                        showSnackbar(t('settings.vat.defaultsSaved', 'De standaard BTW tarieven zijn opgeslagen'));
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'settings.vat.saveDefaultsFailedMsg',
                                        'Het opslaan van de standaard BTW tarieven is niet gelukt.'
                                    )
                            ),
                            t('settings.vat.saveDefaultsFailedTitle', 'Opslaan mislukt')
                        );
                }
            });
        };
    }

    function validateValues(): boolean {
        let valid = true;
        const _errors = {...errors};
        for (const [countryCode, vatValue] of countryValues.entries()) {
            if (vatValue.vat_type !== VATType.CUSTOM) continue;
            if (vatValue.value > 100) {
                _errors[countryCode] = t('settings.vat.errMaxPercent', 'Het percentage kan niet meer zijn dan 100%');
                valid = false;
            } else if (vatValue.value < 0) {
                _errors[countryCode] = t('settings.vat.errNegativePercent', 'Het percentage kan niet negatief zijn');
                valid = false;
            }
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    useEffect(() => {
        const usedCountryCodes = Array.from(countryValues.keys());
        setAvailableCountries(Object.entries(COUNTRIES).filter(entry => !usedCountryCodes.includes(entry[0])));
    }, [countryValues]);

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <DefaultVATSettings
            onClose={onClose}
            onAddCountry={onAddCountry}
            onDeleteCountry={onDeleteCountry}
            onClickChange={onClickChange}
            onChange={onChange}
            onSubmit={onSubmit()}
            countryValues={countryValues}
            loading={loading}
            active={active}
            errors={errors}
            availableCountries={availableCountries}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(DefaultVATSettingsController);
